
import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { AudioDevice } from "@/types/audio/audio";

// Audio Components
import Receive from "./Receive.vue";
import Transmit from "./Transmit.vue";
import { ActiveMapItem, EventDetails } from '@/store/site-monitor/types';
import { SessionResource } from '@/store/sessions/types';

// constants
import AudioLists from './AudioList.vue';
import api from '@/services/api.service';

const deviceServiceRegex = /(^\w+:|^)\/\//;

// vuex modules
const SiteMonitor = namespace("siteMonitor");
const SMCameras = namespace("siteMonitorCameras");
const Session = namespace("sessions");

interface AudioDeviceNearby extends AudioDevice {
    nearby: boolean;
}

@Component({
    components: {
        "receive": Receive,
        "transmit": Transmit,
        "audio-list": AudioLists
    }
})
export default class AudioControl extends Vue {
	@Ref() private nearbyAudioList?: AudioLists;
    @Ref() private areaAudioList?: AudioLists;

    @Session.Getter
    private getSession: (sessionId: number | string) => string;

    @Session.Action
    private updateSession: ({ eventId, resourceId }: { eventId?: number, resourceId: number }) => Promise<void>;

    @SMCameras.State("deviceControllerAudioDevices")
    private nearbyAudioDevices!: ActiveMapItem[];

	@SMCameras.Action("fetchAreaAudios")
    private fetchAreaAudios: (params: { groupID: number, pageNumber: number }) => Promise<void>;

    @SiteMonitor.Getter("getEventDetails")
    private eventDetails: EventDetails;

	@SMCameras.Getter("getAreaAudioDevices")
	private getAreaAudioDevices!: AudioDevice[];

    private selectedAudioDevice: AudioDevice = null;
    private groupAudio: AudioDeviceNearby[] = [];
	private nearbyAudio: AudioDeviceNearby[] = [];
    private filter: string = "";
    private auth: string = "";
	private pageNumber: number = 1;

    private async mounted() {
        this.groupAudio = [];
		this.nearbyAudio = [];
        await this.updateSession({ resourceId: SessionResource.DeviceServiceSession });
        this.auth = this.getSession(SessionResource.DeviceServiceSession);
        await this.fetchNearbyAudio();
		if (this.groupId) {
			await this.fetchGroupAudio(this.groupId, null);
		}
    }

    /**
     * @summary method which will return the DeviceServiceEndpoint
     * this method also strips out the extra bits which could exist like http/s etc.
     */
    private get getDeviceServiceEndpoint(): string {
		if (this.eventDetails?.devicesEndpoint) {
			return this.eventDetails.devicesEndpoint.replace(deviceServiceRegex, '');
		}
        return api.getDeviceServiceAddress().replace(deviceServiceRegex, '');
    }

    /**
     * @summary computed property which will return if at least 1 of the audio types are chosen
     */
    private get loadAudioControls() {
        return (this.selectedAudioDevice) ? true : false;
    }

    /**
     * @summary depending on the components rendered, it will change the column size.
     */
    private get displayControlsWidth(): number {
        return (this.selectedAudioDevice && this.selectedAudioDevice.canReceive && this.selectedAudioDevice.canTransmit) ? 6 : 12;
    }

    // ---------------------------------------------------------------------------
    private get groupId(): number {
        return (this.eventDetails && this.eventDetails.groupID) ? this.eventDetails.groupID : null;
    }

    private get eventId(): number {
        return this.eventDetails ? this.eventDetails.eventID : null;
    }

    @Watch("nearbyAudioDevices", { deep: true })
    private async fetchNearbyAudio(): Promise<void> {
        //filter out any nearby existing audio devices
        this.nearbyAudio = this.nearbyAudio.filter(ad => !ad.nearby);
        const deviceIds = this.nearbyAudioDevices ? this.nearbyAudioDevices.map(ad => ad.objectId) : [];
        if (!deviceIds.length) {
			return;
		}

        const audioDeviceInfo  = await api.fetchAudioDevicesByIds(deviceIds);

        if (!audioDeviceInfo) {
			return;
		}

		this.nearbyAudio = this.nearbyAudio.concat(audioDeviceInfo.audioDevices.map(ad => {
			return { ...ad, nearby: true }
		}));
    }

	@Watch("groupId", { deep: true })
	private async fetchGroupAudio(newGroupId: number | null, oldGroupId: number | null): Promise<void> {
		if (newGroupId == oldGroupId) {
			return;
		}

		if(!newGroupId) {
			return;
		}

		// reset page number
		this.pageNumber = 1;

		await this.fetchAreaAudios({ groupID: newGroupId, pageNumber: this.pageNumber });

		// Remove existing area devices
		this.groupAudio = [];

		// Add new area devices
		const areaDevicesToAdd = this.getAreaAudioDevices.map(d => {
			return { ...d, nearby: false }
		});

		this.groupAudio = this.groupAudio.concat(areaDevicesToAdd);
	}

    private get nearbyDevices(): AudioDeviceNearby[] {
		if (this.filter) {
			return this.nearbyAudio.filter(ad =>
				ad.nearby && ad.title.toLowerCase().includes(this.filter.toLowerCase())
			);
		} else {
			return this.nearbyAudio.filter(ad => ad.nearby);
		}
    }

	private get groupDevices(): AudioDeviceNearby[] {
		if (this.filter) {
			return this.groupAudio.filter(ad =>
				!ad.nearby && ad.title.toLowerCase().includes(this.filter.toLowerCase())
			);
		} else {
			return this.groupAudio.filter(ad => !ad.nearby);
		}
	}

    private get canReceiveAudio(): boolean {
        return this.loadAudioControls && this.selectedAudioDevice.canReceive;
    }

    private get canTransmitAudio(): boolean {
        return this.loadAudioControls && this.selectedAudioDevice.canTransmit;
    }

    public collapseAudioSections(): void {
        this.nearbyAudioList?.collapseAudioList();
        this.areaAudioList?.collapseAudioList();
    }

	private async showMoreAreaAudio(): Promise<void> {
		try {
			await this.fetchAreaAudios({
				groupID: this.groupId,
				pageNumber: this.pageNumber + 1
			});

			this.pageNumber++;

			// merge newly loaded area devices into local groupAudio without duplicates
			const existingIds = this.groupAudio.map(d => d.deviceId);
			const newBatch = this.getAreaAudioDevices
				.filter(d => !existingIds.includes(d.deviceId))
				.map(d => ({ ...d, nearby: false }));

			this.groupAudio = this.groupAudio.concat(newBatch);
		} catch (error) {
			console.error("Failed to load more audio devices:", error);
		}
	}
}
